import {
    inject, Injectable,
} from "@angular/core";
import { IRouterService } from "../interfaces/router-service";
import {
    ActivatedRouteSnapshot, RedirectCommand, ResolveFn, RouterStateSnapshot, UrlTree
} from "@angular/router";
import { ICommonErrorHandlerService } from "../../exceptions/interfaces/common-error-handler";
import { MgdRouterService } from "./mgd-router.service";
import { NavigationSource } from "../enums/navigation-source";
import { COMMON_ERROR_HANDLER } from "../../exceptions/constants/exception-constants";
import { CoreUiException } from "../../exceptions/core-ui-exception";
import { Observable } from "rxjs";

/**
 * The abstract class that all RouterServices extend from
 *
 * A RouterService should be created for each RoutingModule.
 * It is the way that Components, Directives and Services will interact with the router
 *
 * @see IFeatureGroupRouterService
 * @see IRouteData
 * @see MgdRouterService
 */
@Injectable()
export abstract class AbstractRouterService<R extends string> implements IRouterService {

    /** Error handler for managing service-level exceptions */
    protected readonly _errorHandler: ICommonErrorHandlerService = inject(COMMON_ERROR_HANDLER);

    /** Core router service for navigation operations */
    protected readonly _mgdRouter: MgdRouterService = inject(MgdRouterService);

    /**
     * Error messages attached to this route
     *
     * Often used when redirecting to an error page
     *
     * @returns The error message from the active route, if any
     */
    get errorMessage(): string {
        return this._mgdRouter.activeRoute?.errorMessage;
    }

    /**
     * True until the first navigation completes, i.e. while the app is handling a deep link or reload
     *
     * @returns True while no navigation has completed yet
     */
    get isInitialNavigation(): boolean {
        return this._mgdRouter.isInitialNavigation;
    }

    /**
     * Creates a new AbstractRouterService instance
     *
     * @param featureGroupId A unique identifier for the Feature Group. Used to determine if a route
     * this service is concerned with is active. Each route in the module should
     * be tagged with data featureGroupId
     */
    constructor(
        // This is not a DI injection but is passed in from the child
        // eslint-disable-next-line @angular-eslint/prefer-inject
        readonly featureGroupId: string,
    ) {}

    /**
     * Returns true if routeId is the current activated route
     *
     * @param routeId The route identifier to check for active status
     * @returns True if the specified route is currently active, false otherwise
     */
    isRouteActive(routeId: R): boolean {
        return this._mgdRouter.isActiveRouteId(this.featureGroupId, routeId);
    }

    /**
     * Returns the Angular Navigation Command for the given routes
     *
     * If required path params are missing an exception will be thrown
     *
     * Used to populate side nav router links
     *
     * @param routeId The route identifier to get commands for
     * @param pathParams Optional path parameters to substitute in the route
     * @returns Array of route command segments for Angular Router navigation
     * @throws CoreUiException if route is not found or required path params are missing
     */
    getCommands(routeId: R, pathParams?: Record<string, string>): ReadonlyArray<string> {
        try {
            return this._mgdRouter.getCommandsForRoute(this.featureGroupId, routeId, pathParams);
        }
        catch (e) {
            const message = `Error getting commands for ${routeId} in ${this.featureGroupId}: ${(e as Error).message}`;
            this._errorHandler.handleServiceError(e, message);
            throw new CoreUiException(message);
        }
    }

    /**
     * Returns the Angular route path for the given routes
     *
     * If required path params are missing an exception will be thrown
     *
     * Used to populate side nav router links
     *
     * @param routeId The route identifier to get the path for
     * @param pathParams Optional path parameters to substitute in the route
     * @returns The complete route path as a string
     * @throws CoreUiException if route is not found or required path params are missing
     */
    getPath(routeId: R, pathParams?: Record<string, string>): string {
        try {
            return this._mgdRouter.getPathForRoute(this.featureGroupId, routeId, pathParams);
        }
        catch (e) {
            const message = `Error getting path for ${routeId} in ${this.featureGroupId}: ${(e as Error).message}`;
            this._errorHandler.handleServiceError(e, message);
            throw new CoreUiException(message);
        }
    }

    /**
     * Creates an Angular UrlTree for the specified route
     *
     * @param routeId The route identifier to create the UrlTree for
     * @param pathParams Optional path parameters to substitute in the route
     * @returns UrlTree instance for the specified route
     * @throws CoreUiException if route is not found or required path params are missing
     */
    getUrlTree(routeId: R, pathParams?: Record<string, string>): UrlTree {
        try {
            return this._mgdRouter.getUrlTreeForRoute(this.featureGroupId, routeId, pathParams);
        }
        catch (e) {
            const message = `Error getting url tree for ${routeId} in ${this.featureGroupId}: ${(e as Error).message}`;
            this._errorHandler.handleServiceError(e, message);
            throw new CoreUiException(message);
        }
    }

    /**
     * Creates a RedirectCommand for the specified route
     *
     * @param routeId The route identifier to redirect to
     * @param pathParams Optional path parameters to substitute in the route
     * @param source Optional navigation source, defaults to RouterService
     * @returns RedirectCommand instance for the specified route
     * @throws CoreUiException if route is not found or required path params are missing
     */
    getRedirectCommand(routeId: R, pathParams?: Record<string, string>, source?: NavigationSource): RedirectCommand {
        try {
            return this._mgdRouter.getRedirectCommandForRoute(this.featureGroupId, routeId, pathParams, source);
        }
        catch (e) {
            const message = `Error getting redirect command for ${routeId} in ${this.featureGroupId}: ${(e as Error).message}`;
            this._errorHandler.handleServiceError(e, message);
            throw new CoreUiException(message);
        }
    }

    /**
     * Preloads route resolvers and returns their resolved values or a redirect command
     *
     * This function should be called in Guards, since guards execute before data resolvers.
     * It allows you to run resolvers early in the navigation lifecycle and handle any
     * redirects that might be needed based on the resolved data.
     *
     * @param route The activated route snapshot containing route information
     * @param state The router state snapshot containing navigation state
     * @param resolvers Array of resolver functions to execute
     * @returns Observable that emits either an array of resolved values or a RedirectCommand
     * if any resolver returns a redirect
     *
     * @example
     * ```typescript
     * // In a Guard
     * canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | RedirectCommand> {
     *   return this.routerService.preloadResolvers$(route, state, [userResolver, permissionsResolver])
     *     .pipe(
     *       map(result => {
     *         if (result instanceof RedirectCommand) {
     *           return result; // Redirect if needed
     *         }
     *         const [user, permissions] = result;
     *         return this.checkPermissions(user, permissions);
     *       })
     *     );
     * }
     * ```
     */
    preloadResolvers$(route: ActivatedRouteSnapshot, state: RouterStateSnapshot, resolvers: Array<ResolveFn<unknown>>):
    Observable<Array<unknown> | RedirectCommand> {
        return this._mgdRouter.preloadResolvers$(route, state, resolvers);
    }

    /**
     * Navigate to the routeId
     *
     * If the routeId isn't found, an error is thrown
     *
     * If the route requires PathParams and they are not provided an error is thrown
     *
     * If navigationSource is not provided it will default to RouterService
     *
     * @param routeId The route identifier to navigate to
     * @param pathParams Optional path parameters to substitute in the route
     * @param queryParams Optional query parameters to include in the navigation
     * @param navigationSource Optional source of the navigation, defaults to RouterService
     * @throws CoreUiException if route is not found or required path params are missing
     */
    protected _navigateByRouteId(
        routeId: R, pathParams?: Record<string, string>, queryParams?: Record<string, string>, navigationSource?: NavigationSource
    ): void {
        this._mgdRouter.navigateByRouteId(this.featureGroupId, routeId, pathParams, queryParams, navigationSource);
    }
}
